import { animatedSpriteUrl, spriteUrl } from "../styles/theme";

export interface PokemonSummary {
  id: number;
  name: string;
}

export interface PokemonStat {
  name: string;
  value: number;
}

export interface PokemonDetails {
  id: number;
  name: string;
  types: string[];
  stats: PokemonStat[];
  sprite: string;
  animatedSprite: string;
  height: number;
  weight: number;
  genus: string;
  flavorText: string;
}

/** Kept for unused legacy components. */
export interface Pokemon {
  id: number;
  name: string;
  types: Array<{
    type: {
      name: string;
    };
  }>;
  sprites: {
    front_default: string;
  };
  stats: Array<{
    base_stat: number;
    stat: {
      name: string;
    };
  }>;
}

export interface PokemonListResponse {
  results: Array<{
    name: string;
    url: string;
  }>;
}

const BASE_URL = "https://pokeapi.co/api/v2";

const idFromUrl = (url: string) => {
  const parts = url.split("/").filter(Boolean);
  return Number(parts[parts.length - 1]);
};

const cleanFlavorText = (text: string) =>
  text
    .replace(/\f/g, " ")
    .replace(/[\n\r]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const pickFlavorText = (
  entries: Array<{
    flavor_text: string;
    language: { name: string };
    version: { name: string };
  }>
) => {
  const english = entries.filter((entry) => entry.language.name === "en");
  const preferred = ["red", "blue", "yellow", "firered", "leafgreen"];
  for (const version of preferred) {
    const match = english.find((entry) => entry.version.name === version);
    if (match) {
      return cleanFlavorText(match.flavor_text);
    }
  }
  return english[0] ? cleanFlavorText(english[0].flavor_text) : "";
};

type PokemonApi = {
  id: number;
  name: string;
  height: number;
  weight: number;
  types: Array<{ type: { name: string } }>;
  stats: Array<{ base_stat: number; stat: { name: string } }>;
  sprites: { front_default: string | null };
};

type SpeciesApi = {
  genera: Array<{ genus: string; language: { name: string } }>;
  flavor_text_entries: Array<{
    flavor_text: string;
    language: { name: string };
    version: { name: string };
  }>;
};

const detailsCache = new Map<number, PokemonDetails>();

const pokeApi = {
  BASE_URL,

  async getPokemonList(limit: number): Promise<PokemonSummary[]> {
    const response = await fetch(`${BASE_URL}/pokemon?limit=${limit}`);
    if (!response.ok) {
      throw new Error("Failed to fetch Pokémon list");
    }
    const data: PokemonListResponse = await response.json();
    return data.results.map((pokemon) => ({
      id: idFromUrl(pokemon.url),
      name: pokemon.name,
    }));
  },

  async getPokemonById(id: number): Promise<Pokemon> {
    const response = await fetch(`${BASE_URL}/pokemon/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch Pokémon with ID ${id}`);
    }
    return response.json();
  },

  async getPokemonDetails(id: number): Promise<PokemonDetails> {
    const cached = detailsCache.get(id);
    if (cached) {
      return cached;
    }

    const [pokemonRes, speciesRes] = await Promise.all([
      fetch(`${BASE_URL}/pokemon/${id}`),
      fetch(`${BASE_URL}/pokemon-species/${id}`),
    ]);

    if (!pokemonRes.ok || !speciesRes.ok) {
      throw new Error(`Failed to fetch Pokédex entry #${id}`);
    }

    const pokemon: PokemonApi = await pokemonRes.json();
    const species: SpeciesApi = await speciesRes.json();
    const genus =
      species.genera.find((entry) => entry.language.name === "en")?.genus ?? "";

    const details: PokemonDetails = {
      id: pokemon.id,
      name: pokemon.name,
      types: pokemon.types.map((entry) => entry.type.name),
      stats: pokemon.stats.map((entry) => ({
        name: entry.stat.name,
        value: entry.base_stat,
      })),
      sprite: pokemon.sprites.front_default ?? spriteUrl(pokemon.id),
      animatedSprite: animatedSpriteUrl(pokemon.id),
      height: pokemon.height,
      weight: pokemon.weight,
      genus,
      flavorText: pickFlavorText(species.flavor_text_entries),
    };

    detailsCache.set(id, details);
    return details;
  },
};

export default pokeApi;
